<template>
    <MainContainer>
        <template v-slot:sidebar>&nbsp;</template>
        <div class="werke">
            <h2>
                Werke Stammdaten
                <b-button
                    v-if="checkRight('write')"
                    :size="size"
                    @click="dataCreate"
                >
                    Neu
                    <font-awesome-icon icon="plus" />
                </b-button>
            </h2>
            <Table
                bordered
                striped
                small
                showFilter
                sortBy="verfallsdatum"
                :sortDesc="true"
                :fields="fields"
                :items="items"
                :isBusy="busy"
                busyText="Lädt Werke..."
                emptyText="Keine Werke gefunden"
            >
                <template v-slot:datum="{ row: row }">
                    {{ $moment(row.value).format("DD.MM.Y") }}
                </template>
                <template v-slot:verfallsdatum="{ row: row }">
                    {{ $moment(row.value).format("DD.MM.Y") }}
                </template>
                <template v-slot:oeffentlich="{ row: row }">
                    <template v-if="row.value == 1">
                        <font-awesome-icon icon="check" />
                    </template>
                    <template v-else>
                        <font-awesome-icon icon="times" />
                    </template>
                </template>

                <template v-slot:actions="{ row: row }">
                    <b-button
                        v-if="checkRight('write')"
                        :size="size"
                        variant="link"
                        @click="dataSelected(row)"
                        class="mv-table-action-btn mr-1"
                        >Bearbeiten</b-button
                    >
                    <b-button
                        v-if="checkRight('delete')"
                        :size="size"
                        @click="dataDelete(row)"
                        variant="link"
                        class="text-danger mv-table-action-btn"
                        >Löschen</b-button
                    >
                </template>
            </Table>
            <EditModal
                :title="selected.title"
                :busy="selected.busy"
                size="xl"
                id="newModal"
                buttonText="Anlegen"
                scrollable
                @ok="dataProvide"
                @hide="dataHide"
            >
                <b-tabs pills card vertical>
                    <b-tab title="Werkdaten" active>
                        <template v-if="selected.data">
                            <FormGroupBuilder
                                :fields="ioFields.new"
                                :data="selected.data"
                                :refresh="validationRefresh"
                            />
                        </template>
                    </b-tab>
                    <b-tab title="Pflichtproben">
                        <template v-if="selected.data">
                            <FormGroupBuilder
                                :fields="ioFields.pflicht"
                                :data="selected.data"
                                :refresh="validationRefresh"
                            />
                        </template>
                    </b-tab>
                </b-tabs>
            </EditModal>
            <EditModal
                :title="selected.title"
                :busy="selected.busy"
                size="xl"
                buttonText="Speichern"
                scrollable
                @ok="dataProvide"
                @hide="dataHide"
            >
                <b-tabs pills card vertical>
                    <b-tab title="Werkdaten" active>
                        <template v-if="selected.data">
                            <FormGroupBuilder
                                :fields="ioFields.edit"
                                :data="selected.data"
                                :refresh="validationRefresh"
                            />
                        </template>
                    </b-tab>
                    <b-tab title="Pflichtproben">
                        <template v-if="selected.data">
                            <FormGroupBuilder
                                :fields="ioFields.pflicht"
                                :data="selected.data"
                            />
                        </template>
                    </b-tab>
                    <b-tab title="Beteiligte Mitglieder">
                        <template v-if="selected.data">
                            <SingerSelect
                                :id="selected.data.id"
                                @update="updateMitglieder"
                            />
                        </template>
                    </b-tab>
                </b-tabs>
            </EditModal>
            <RemoveModal
                :title="selected.title"
                :busy="selected.busy"
                @ok="dataProvide"
                @hide="dataHide"
            />
        </div>
    </MainContainer>
</template>

<script>
import CommonView from "@/mixins/CommonView/CommonView";
import Size from "@/mixins/Size/Size";
import Validation from "@/mixins/Validation/Validation";
import { ApiFactory } from "@/api/ApiFactory.js";
import MainContainer from "@/components/MainContainer/MainContainer";
import Table from "@/components/Table/Table";
import EditModal from "@/components/EditModal/EditModal";
import RemoveModal from "@/components/RemoveModal/RemoveModal";
import FormGroupBuilder from "@/components/FormGroupBuilder/FormGroupBuilder";
import SingerSelect from "@/components/SingerSelect/SingerSelect";

const WerkeApi = ApiFactory.get("werke");
const MitgliederApi = ApiFactory.get("mitglieder");

export default {
    name: "Werke",
    mixins: [CommonView, Size, Validation],
    components: {
        MainContainer,
        Table,
        EditModal,
        RemoveModal,
        FormGroupBuilder,
        SingerSelect
    },
    data() {
        return {
            fields: [
                {
                    key: "id",
                    label: "ID",
                    sortable: true
                },
                {
                    key: "such",
                    label: "Kürzel",
                    sortable: true
                },
                /*{
                    key: "datum",
                    label: "Datum",
                    sortable: true
                },*/
                {
                    key: "bezeichnung",
                    label: "Name",
                    sortable: true
                },
                {
                    key: "verfallsdatum",
                    label: "Ablaufdatum",
                    sortable: true
                },
                {
                    key: "oeffentlich",
                    label: "öffentlich",
                    class: "text-center"
                },
                {
                    key: "actions",
                    label: "Funktionen",
                    class: "text-center"
                }
            ],
            ioFields: {
                new: [
                    {
                        type: "notice",
                        text:
                            "Wenn ein neues Werk angelegt wird, können in diesem Schritt dem Werk noch keine Mitglieder zugeordnet werden. Deshalb muss in einem zweiten Schritt das angelegte Werk ausgewählt werden, und dann die Mitglieder zugeordnet werden.",
                        variant: "warning"
                    },
                    {
                        key: "id",
                        label: "ID",
                        type: "text",
                        disabled: true,
                        oneWay: true
                    },
                    {
                        key: "such",
                        label: "Kürzel",
                        type: "text",
                        mandatory: true,
                        placeholder: "#KÜRZEL#",
                        description:
                            "Die Kürzel werden momentan für die Verknüpfung von Sängern und Werken genutzt. Sie müssen der Form #KÜRZEL# entsprechen!"
                    },
                    /*{
                        key: "datum",
                        label: "Datum",
                        type: "date",
                        mandatory: true,
                        description: "Datum des ersten Konzerts dieses Werkes"
                    },*/
                    {
                        key: "bezeichnung",
                        label: "Name",
                        mandatory: true,
                        placeholder: "z.B. Mahler: Symphonie Nr. 8",
                        type: "text"
                    },
                    {
                        key: "verfallsdatum",
                        label: "Ablaufdatum",
                        mandatory: true,
                        type: "date",
                        description: "Datum des letzten Konzerts dieses Werkes"
                    },
                    {
                        key: "oeffentlich",
                        label: "öffentlich",
                        type: "checkbox",
                        checked_value: 1,
                        unchecked_value: 0,
                        description:
                            "Durch die Veröffentlichung von Werken erscheinen diese auch auf der Homepage mit den zugehörigen Sänger*INNEN"
                    },
                    {
                        key: "chorleitung",
                        label: "Chorleitung",
                        type: "mitgliedermultiselect",
                        description:
                            "Die angegebene Chorleitung wird ebenfalls in der öffentlichen Konzertbesetzung gezeigt. WICHTIG: Das angegebene Mitglied muss nicht dem Konzert zugeordnet sein!"
                    },
                    {
                        key: "korrepetitor",
                        label: "Korrepetitor",
                        type: "mitgliedermultiselect",
                        description:
                            "Der/die angegebene Korrepetitor wird ebenfalls in der öffentlichen Konzertbesetzung gezeigt. WICHTIG: Das angegebene Mitglied muss nicht dem Konzert zugeordnet sein!"
                    },
                    {
                        key: "stimmbildung",
                        label: "Stimmbildung",
                        type: "mitgliedermultiselect",
                        description:
                            "Die angegebene Stimmbildung wird ebenfalls in der öffentlichen Konzertbesetzung gezeigt. WICHTIG: Das angegebene Mitglied muss nicht dem Konzert zugeordnet sein!"
                    }
                ],
                edit: {
                    id: {
                        key: "id",
                        label: "ID",
                        type: "text",
                        disabled: true,
                        oneWay: true
                    },
                    such: {
                        key: "such",
                        label: "Kürzel",
                        type: "text",
                        mandatory: true,
                        placeholder: "z.B. mahler8",
                        description:
                            "Die Kürzel werden momentan für die Verknüpfung von Sängern und Werken genutzt. Sie müssen zusammenhängend sein, dürfen keine Leerzeichen enthalten und müssen der Form #KÜRZEL# mit einer führenden und einer abschließenden # geschrieben werden"
                    },
                    /*{
                        key: "datum",
                        label: "Datum",
                        mandatory: true,
                        type: "date",
                        description: "Datum des ersten Konzerts dieses Werkes"
                    },*/
                    bezeichnung: {
                        key: "bezeichnung",
                        label: "Bezeichnung des Werkes",
                        mandatory: true,
                        placeholder: "Bezeichnung des Werkes",
                        type: "text"
                    },
                    verfallsdatum: {
                        key: "verfallsdatum",
                        label: "Ablaufdatum",
                        mandatory: true,
                        type: "date",
                        description: "Datum des letzten Konzerts dieses Werkes"
                    },
                    oeffentlich: {
                        key: "oeffentlich",
                        label: "öffentlich",
                        type: "checkbox",
                        checked_value: 1,
                        unchecked_value: 0,
                        description:
                            "Durch die Veröffentlichung von Werken erscheinen diese auch auf der Homepage mit den zugehörigen Sänger*INNEN"
                    },
                    chorleitung: {
                        key: "chorleitung",
                        label: "Chorleitung",
                        type: "mitgliedermultiselect",
                        chorExclusive: true,
                        description:
                            "Die angegebene Chorleitung wird ebenfalls in der öffentlichen Konzertbesetzung gezeigt. WICHTIG: Das angegebene Mitglied muss nicht dem Konzert zugeordnet sein!"
                    },
                    korrepetitor: {
                        key: "korrepetitor",
                        label: "Korrepetitor",
                        type: "mitgliedermultiselect",
                        chorExclusive: true,
                        description:
                            "Der/die angegebene Korrepetitor wird ebenfalls in der öffentlichen Konzertbesetzung gezeigt. WICHTIG: Das angegebene Mitglied muss nicht dem Konzert zugeordnet sein!"
                    },
                    stimmbildung: {
                        key: "stimmbildung",
                        label: "Stimmbildung",
                        type: "mitgliedermultiselect",
                        chorExclusive: true,
                        description:
                            "Die angegebene Stimmbildung wird ebenfalls in der öffentlichen Konzertbesetzung gezeigt. WICHTIG: Das angegebene Mitglied muss nicht dem Konzert zugeordnet sein!"
                    }
                },
                pflicht: {
                    pflicht_ko: {
                        key: "pflicht_ko",
                        label: "Konzertteilnahme (Pflicht)",
                        append: "%",
                        description:
                            "Anteil der Teilnahmepflicht an allen Konzertterminen dieses Werkes in %"
                    },
                    pflicht_general: {
                        key: "pflicht_general",
                        label: "Probenteilnahme (Pflicht)",
                        append: "%",
                        description:
                            "Anteil der Teilnahmepflicht an allen Proben dieses Werkes in %. Diese Angabe ist für alle Stimmen gültig, so lange keine abweichenden Angaben für Einzelstimmen in den Feldern unten gemach werden"
                    },
                    hr: {
                        key: "",
                        type: "hr"
                    },
                    pflicht_sopran: {
                        key: "pflicht_sopran",
                        label: "Sopranproben (Pflicht)",
                        append: "%"
                    },
                    pflicht_alt: {
                        key: "pflicht_alt",
                        label: "Altproben (Pflicht)",
                        append: "%"
                    },
                    pflicht_tenor: {
                        key: "pflicht_tenor",
                        label: "Tenorproben (Pflicht)",
                        append: "%"
                    },
                    pflicht_bass: {
                        key: "pflicht_bass",
                        label: "Bassproben (Pflicht)",
                        append: "%"
                    }
                }
            },
            api: WerkeApi,
            mitglieder: []
        };
    },
    watch: {
        selected: {
            handler(newVal, oldVal) {
                if (newVal.data) {
                    const pflicht_general = newVal.data.pflicht_general;
                    if (
                        newVal.data.pflicht_sopran === null ||
                        typeof newVal.data.pflicht_sopran === "undefined"
                    ) {
                        this.ioFields.pflicht.pflicht_sopran.placeholder = String(
                            pflicht_general
                        );
                    }
                    if (
                        newVal.data.pflicht_alt === null ||
                        typeof newVal.data.pflicht_alt === "undefined"
                    ) {
                        this.ioFields.pflicht.pflicht_alt.placeholder = String(
                            pflicht_general
                        );
                    }
                    if (
                        newVal.data.pflicht_tenor === null ||
                        typeof newVal.data.pflicht_tenor === "undefined"
                    ) {
                        this.ioFields.pflicht.pflicht_tenor.placeholder = String(
                            pflicht_general
                        );
                    }
                    if (
                        newVal.data.pflicht_bass === null ||
                        typeof newVal.data.pflicht_bass === "undefined"
                    ) {
                        this.ioFields.pflicht.pflicht_bass.placeholder = String(
                            pflicht_general
                        );
                    }
                }
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        dataCreate() {
            const newObj = {
                pflicht_general: 100,
                pflicht_ko: 100
            };
            this.dataSet(newObj, "new", "Neu erstellen");
            this.$root.$emit("bv::show::modal", "newModal");
        },
        updateMitglieder(value) {
            this.mitglieder = value;
        },
        async additionalRequests(selectedItem) {
            const response = await WerkeApi.putMitglieder(
                selectedItem.id,
                this.mitglieder
            );
            return response.ok;
        },
        fetchErrorHandling(response) {
            if (this.selected.action === "new") {
                this.handleValidationData(response, this.ioFields.new);
            } else {
                this.handleValidationData(response, this.ioFields.edit);
            }
        },
        postDataSet() {
            if (this.selected.action === "new") {
                this.cleanValidationData(this.ioFields.new);
            } else {
                this.cleanValidationData(this.ioFields.edit);
            }
        }
    }
};
</script>
